import { T_KeyValue } from "@/types/app.type";
import { CAT, GRAPH_QL_URL, CPAT, REST_API_URL, REST_PREVIEW_API_URL, AZURE_FUNCTION_SECRET, AZURE_FUNC_ENDPOINT, CONTENTFUL_SPACE_ID } from "../config/app.config";
import { ApiResponse } from "../helpers/response.helper";

const CONTENTFUL_PREVIEW_ACCESS_TOKEN = CPAT || process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW_ACCESS_TOKEN || "";
const CONTENTFUL_ACCESS_TOKEN = CAT || process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN || "";

/**
 * Name: graphQL
 * 
 * Descrition: makes a POST API call using fetch to Contentful using graphQL API
 *  
 * @param query contains the payload to sent along with the API call
 * @param preview boolean flag; if true, passes the Content Preview API access token in API header; if false, passes the Content Delivery API access token in the API header
 * @returns returns a Promise containing the API response
 */

const graphQL = async (query: T_KeyValue, preview: boolean = false) => {

    try {
        return fetch(
            `${GRAPH_QL_URL}`, {
                signal: AbortSignal.timeout(10000),
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                // Switch the Bearer token depending on whether the fetch is supposed to retrieve live
                // Contentful content or draft content
                Authorization: `Bearer ${preview
                    ? CONTENTFUL_PREVIEW_ACCESS_TOKEN
                    : CONTENTFUL_ACCESS_TOKEN
                    }`,
                'Cache-Control': 'public, max-age=0, must-revalidate'
            },
            body: JSON.stringify({ query }),
            // Associate all fetches for articles with an "articles" cache tag so content can
            // be revalidated or updated from Contentful on publish
            next: { tags: ["articles"] },
        }
        ).then((response) => response.json());
    } catch (error) {
        return ApiResponse({ error: error });
    }

}


// Rest APIs

/**
 * Name: postContent
 * 
 * Description: makes a POST API call using fetch to Contentful using the REST API provided by Contentful
 * 
 * @param data contains the payload to sent along with the API call
 * @param preview boolean flag; if true, passes the Content Preview API access token in API header; if false, passes the Content Delivery API access token in the API header
 * @returns returns a Promise containing the API response
 */
const postContent = async (data: T_KeyValue, preview: boolean = false) => {

    try {
        return fetch(
            `${preview ? REST_PREVIEW_API_URL : REST_API_URL}/content_types`, 
            {
                signal: AbortSignal.timeout(10000),
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    // Switch the Bearer token depending on whether the fetch is supposed to retrieve live
                    // Contentful content or draft content
                    Authorization: `Bearer ${preview
                        ? CONTENTFUL_PREVIEW_ACCESS_TOKEN
                        : CONTENTFUL_ACCESS_TOKEN
                        }`,
                    'Cache-Control': 'public, max-age=0, must-revalidate'
                },
                body: JSON.stringify(data),
                // Associate all fetches for articles with an "articles" cache tag so content can
                // be revalidated or updated from Contentful on publish
                // next: { tags: ["articles"] },
            }
        ).then((response) => {
            // response.json()
            return ApiResponse({ res: response.json() });
        });
    } catch (error) {
        return ApiResponse({ error: error });
    }
}


/** defines the type of the props to be passed in getContent and getContentType API functions */
type T_tpContent = {
    /** path parameters in API call */
    pathParams?: string[],

    /** query parameters in API call */
    queryParams?: {
        [key: string]: T_KeyValue | string | number
    },

    /**boolean flag; if true, passes the Content Preview API access token in API header; if false, passes the Content Delivery API access token in the API header */
    preview?: boolean
}

/**
 * Name: getContent
 * 
 * Description: makes a GET API call using fetch to Contentful using the REST API provided by Contentful; it fetches the content of a given content type
 *
 * @param param0 an object that contains pathParams, queryParams and preview. These are parameters to be used in the API call
 * 
 * @returns returns a Promise containing the API response
 */
const getContent = async ({ pathParams = [], queryParams = {}, preview = false }: T_tpContent) => {
    // console.log("preview enabled --- ", preview);

    let params: string = '';
    let delim: string = '?';
    Object.keys(queryParams || {}).forEach(key => {
        params += `${delim}${key}=${queryParams[key]}`;
        delim = "&";
    });
    let url: string = `${preview ? REST_PREVIEW_API_URL : REST_API_URL}/entries`;
    if (pathParams && pathParams.length > 0) {
        url += `/${pathParams.join("/")}`;
    }
    url += `${params}`;


    // console.log("url --- ", url);
    try {
        if(CONTENTFUL_SPACE_ID) {
            return fetch(
                url,
                {
                    signal: AbortSignal.timeout(30000),
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        // Switch the Bearer token depending on whether the fetch is supposed to retrieve live
                        // Contentful content or draft content
                        Authorization: `Bearer ${preview
                            ? CONTENTFUL_PREVIEW_ACCESS_TOKEN
                            : CONTENTFUL_ACCESS_TOKEN
                            }`,
                        'Cache-Control': 'public, max-age=0, must-revalidate'
                    },
                    // prefetch: true,
                    cache: 'no-store', // stop cache
                    // cache: 'force-cache',
                    // next: { revalidate: 5 * 60 } // revalidating data after every 5 minute from server
                }
            ).then(async (response) => {
                // response.json()
                const json = await response.clone().json();
                return ApiResponse({ res: json });
            });
        }
    } catch (error) {
        return ApiResponse({ error: error });
    }
}

const getActualContent = async ({ pathParams = [], queryParams = {}, preview = false }: T_tpContent) => {
    // console.log("preview enabled --- ", preview);

    let params: string = '';
    let delim: string = '?';
    Object.keys(queryParams || {}).forEach((key: string) => {
        params += `${delim}${key}=${queryParams[key]}`;
        delim = "&";
    });
    let url: string = `${preview ? REST_PREVIEW_API_URL : REST_API_URL}/entries`;
    if (pathParams && pathParams.length > 0) {
        url += `/${pathParams.join("/")}`;
    }
    url += `${params}`;


    try {
        if (CONTENTFUL_SPACE_ID) {
            return fetch(
                url,
                {
                    signal: AbortSignal.timeout(30000),
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        // Switch the Bearer token depending on whether the fetch is supposed to retrieve live
                        // Contentful content or draft content
                        Authorization: `Bearer ${preview
                            ? CONTENTFUL_PREVIEW_ACCESS_TOKEN
                            : CONTENTFUL_ACCESS_TOKEN
                            }`,
                        'Cache-Control': 'public, max-age=0, must-revalidate'
                    },
                    // prefetch: true,
                    cache: 'no-store', // stop cache
                    // cache: 'force-cache',
                    // next: { revalidate: 5 * 60 } // revalidating data after every 5 minute from server
                }
            ).then(async (response) => {
                // response.json()
                const json = await response.clone().json();
                return { res: json };
            });
        }
    } catch (error) {
        return ApiResponse({ error: error });
    }
}

/**
 * Name: getContentType
 * 
 * Description: makes a GET API call using fetch to Contentful using the REST API provided by Contentful; it fetches the field structure of a given content type
 * 
 * @param param0 an object that contains pathParams, queryParams and preview. These are parameters to be used in the API call
 * 
 * @returns returns a Promise containing the API response
 */
const getContentType = async ({ pathParams = [], queryParams = {}, preview = false }: T_tpContent) => {

    let params: string = '';
    let delim: string = '?';
    Object.keys(queryParams || {}).forEach((key: string) => {
        params += `${delim}${key}=${queryParams[key]}`;
        delim = "&";
    });
    let url: string = `${preview ? REST_PREVIEW_API_URL : REST_API_URL}/content_types`;
    if (pathParams && pathParams.length > 0) {
        url += `/${pathParams.join("/")}`;
    }
    url += `${params}`;

    try {
        return fetch(
            url,
            {
                signal: AbortSignal.timeout(30000),
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    // Switch the Bearer token depending on whether the fetch is supposed to retrieve live
                    // Contentful content or draft content
                    Authorization: `Bearer ${preview
                        ? CONTENTFUL_PREVIEW_ACCESS_TOKEN
                        : CONTENTFUL_ACCESS_TOKEN
                        }`,
                    'Cache-Control': 'public, max-age=0, must-revalidate'
                },
                // prefetch: true,
                cache: 'no-store', // stop cache
                // cache: 'force-cache',
                // next: { revalidate: 5 * 60 } // revalidating data after every 5 minute from server
            }
        ).then(async (response) => {
            const json = await response.clone().json();
            return ApiResponse({ res: json, contentType: true });
        })
    } catch (error) {
        return ApiResponse({ error: error });
    }
}



/** Defines the type of the props to be passed in getFeedPostData API functions */
type T_getProductFeedData = {
    "rating_value":number,
    "breathtakingitem":boolean,
    "isNew":boolean,
    "isTopSeller":boolean,
    "isLastChance":boolean,
    "newLowerPrice":boolean,
    "isLimitEdition":boolean,
    "timeRestrictedOffer":boolean,
    "numberOfItem":number,
    "businessUnitCode":string,
    "primaryLang":string,
    "locale":string,
}


/**
 * Name: getFeedPostData
 * 
 * Description: makes a POST api call to get the data of Products
 * 
 * @param param0 an object that contains pathParams, queryParams and preview. These are parameters to be used in the API call.
 * 
 * @returns return a Promise containing the API response
 * 
 */

const getProductFeedData = async (payload:T_getProductFeedData) =>{
    try {
        const response = await fetch(
            `${AZURE_FUNC_ENDPOINT}/api/GetData`, 
            {
                signal: AbortSignal.timeout(10000),
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-functions-key":AZURE_FUNCTION_SECRET,
                    'Cache-Control': 'public, max-age=0, must-revalidate'
                },
                body: JSON.stringify(payload),
            }
        );
        const json = await response.json();
        return { res: json };

    } catch (error) {
        return ApiResponse({error: error}) ;
    }
}

/**
 * Name: postConfirmBooking
 * 
 * Description: makes a POST api call to confirm a booking
 * * @param payload an object that contains bookingId, resourceId and meetingPlaceId.
 * * @returns return a object with validBooking boolean flag in the response
 */
type ConfirmBookingPayload = {
  resourceId?: string;
  bookingId: string;
  meetingPlaceId?: string;
};

const postConfirmBooking = async (payload:ConfirmBookingPayload ) =>{
    try {
        // Dummy api call simulation for booking confirmation
        const response = await new Promise((resolve) => {   
            return setTimeout(() => {
                resolve({validBooking: true});
            }, 4000);
        });

        // const response = await fetch(
        //     `/api/confirmBookingEndpoint`, 
        //     {
        //         signal: AbortSignal.timeout(10000),
        //         method: "POST",
        //         headers: {
        //             "Content-Type": "application/json",
        //             'Cache-Control': 'public, max-age=0, must-revalidate'
        //         },
        //         body: JSON.stringify(payload),
        //     }
        // );
        // const json = await response.json();
        return { res: response };
    } catch (error) {
        return ApiResponse({error: error}) ;
    }
}

export const API = {
    graphQL,
    getContentType,
    postContent,
    getContent,
    getActualContent,
    getProductFeedData,
    postConfirmBooking
}